import React, { useState } from 'react';
import { CheckCircleIcon, AlertTriangleIcon, ClockIcon, ArrowLeftIcon, FileTextIcon, CalendarIcon, CheckIcon, XIcon, SendIcon, CreditCardIcon, ShieldIcon, AlertCircleIcon } from 'lucide-react';
import { FileUpload } from '../FileUpload';
export function ComplianceCheck({
  vendor,
  onUpdate,
  setCurrentView,
  locale = 'en-US'
}) {
  const [selectedVendor, setSelectedVendor] = useState(vendor || null);
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState('');
  // If no vendor is selected, show a list of vendors with compliance issues
  const vendorsWithIssues = [{
    id: 'VEN-2023-002',
    name: 'Dell Technologies',
    status: 'Active',
    complianceStatus: 'Pending Review',
    type: 'IT Equipment',
    country: 'United States',
    onboardingDate: '2023-02-10',
    lastUpdated: '2023-10-05',
    issue: 'Insurance Certificate expired on 2023-10-10',
    documents: [{
      id: 1,
      name: 'Insurance Certificate',
      type: 'Insurance',
      uploadDate: '2023-02-10',
      expiryDate: '2023-10-10',
      status: 'Expired'
    }],
    bankVerification: {
      status: 'Verified',
      lastChecked: '2023-09-20',
      issues: []
    }
  }, {
    id: 'VEN-2023-003',
    name: 'PrintWorks Inc.',
    status: 'Pending Onboarding',
    complianceStatus: 'Incomplete',
    type: 'Printing Services',
    country: 'Canada',
    onboardingDate: '2023-09-30',
    lastUpdated: '2023-09-30',
    issue: 'Missing required documents',
    documents: [],
    bankVerification: {
      status: 'Not Verified',
      lastChecked: null,
      issues: [{
        severity: 'High',
        description: 'Bank account information not provided'
      }]
    }
  }, {
    id: 'VEN-2023-006',
    name: 'Global Supplies Ltd',
    status: 'Active',
    complianceStatus: 'Non-Compliant',
    type: 'Office Supplies',
    country: 'United Kingdom',
    onboardingDate: '2023-04-15',
    lastUpdated: '2023-10-18',
    issue: "Bank details don't match Eftsure verification",
    documents: [{
      id: 1,
      name: 'Insurance Certificate',
      type: 'Insurance',
      uploadDate: '2023-04-15',
      expiryDate: '2024-04-15',
      status: 'Valid'
    }],
    bankVerification: {
      status: 'Failed',
      lastChecked: '2023-10-18',
      issues: [{
        severity: 'Critical',
        description: "Account name doesn't match registered business name"
      }, {
        severity: 'High',
        description: "BSB/Routing number doesn't match registered bank"
      }]
    }
  }, {
    id: 'VEN-2023-007',
    name: 'TechCore Systems',
    status: 'Active',
    complianceStatus: 'Non-Compliant',
    type: 'IT Services',
    country: 'Australia',
    onboardingDate: '2023-05-22',
    lastUpdated: '2023-10-15',
    issue: 'Multiple compliance issues including bank verification',
    documents: [{
      id: 1,
      name: 'Business Registration',
      type: 'Legal',
      uploadDate: '2023-05-22',
      expiryDate: null,
      status: 'Valid'
    }, {
      id: 2,
      name: 'Insurance Certificate',
      type: 'Insurance',
      uploadDate: '2023-05-22',
      expiryDate: '2023-09-22',
      status: 'Expired'
    }],
    bankVerification: {
      status: 'Warning',
      lastChecked: '2023-10-15',
      issues: [{
        severity: 'Medium',
        description: 'Account details partially match - manual verification required'
      }]
    }
  }, {
    id: 'VEN-2023-008',
    name: 'Stellar Logistics',
    status: 'Active',
    complianceStatus: 'Non-Compliant',
    type: 'Logistics',
    country: 'United States',
    onboardingDate: '2023-03-10',
    lastUpdated: '2023-10-10',
    issue: 'Sanctions list match found',
    documents: [{
      id: 1,
      name: 'Business License',
      type: 'Legal',
      uploadDate: '2023-03-10',
      expiryDate: '2024-03-10',
      status: 'Valid'
    }],
    bankVerification: {
      status: 'Verified',
      lastChecked: '2023-10-10',
      issues: []
    },
    sanctionsCheck: {
      status: 'Failed',
      lastChecked: '2023-10-10',
      issues: [{
        severity: 'Critical',
        description: 'Potential match found on international sanctions list'
      }]
    }
  }];
  const handleFileUpload = newFiles => {
    setFiles([...files, ...newFiles]);
  };
  const removeFile = index => {
    setFiles(files.filter((_, i) => i !== index));
  };
  const handleUpdateStatus = status => {
    if (!selectedVendor) return;
    // Process new documents if any
    const newDocuments = files.map((file, index) => ({
      id: selectedVendor.documents.length + index + 1,
      name: file.name,
      type: 'Compliance Document',
      uploadDate: new Date().toISOString().split('T')[0],
      expiryDate: null,
      status: 'Valid'
    }));
    // Update vendor with new compliance status and documents
    const updatedVendor = {
      ...selectedVendor,
      complianceStatus: status,
      documents: [...selectedVendor.documents, ...newDocuments],
      lastUpdated: new Date().toISOString().split('T')[0]
    };
    onUpdate(updatedVendor);
    setCurrentView('vendors');
  };
  const handleSendReminder = () => {
    // In a real app, this would send an email to the vendor
    alert(`Reminder sent to ${selectedVendor.name} to update their compliance documents.`);
  };
  const getComplianceBadge = status => {
    switch (status) {
      case 'Compliant':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-21102">
            <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-21103" />
            {status}
          </span>;
      case 'Pending Review':
      case 'Incomplete':
      case 'Warning':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-21104">
            <ClockIcon className="mr-1 h-3 w-3" data-id="element-21105" />
            {status}
          </span>;
      case 'Non-Compliant':
      case 'Expired':
      case 'Failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-21106">
            <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-21107" />
            {status}
          </span>;
      case 'Verified':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-21108">
            <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-21109" />
            {status}
          </span>;
      case 'Not Verified':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-21110">
            <ClockIcon className="mr-1 h-3 w-3" data-id="element-21111" />
            {status}
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-21112">
            {status}
          </span>;
    }
  };
  const getDocumentStatusBadge = status => {
    switch (status) {
      case 'Valid':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-21113">
            <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-21114" />
            {status}
          </span>;
      case 'Pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-21115">
            <ClockIcon className="mr-1 h-3 w-3" data-id="element-21116" />
            {status}
          </span>;
      case 'Expired':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-21117">
            <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-21118" />
            {status}
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-21119">
            {status}
          </span>;
    }
  };
  const getIssueIcon = issue => {
    if (issue.description.includes('bank') || issue.description.includes('account')) {
      return <CreditCardIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-orange-500" data-id="element-21120" />;
    } else if (issue.description.includes('sanctions') || issue.description.includes('background')) {
      return <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-red-500" data-id="element-21121" />;
    } else if (issue.description.includes('expired')) {
      return <ClockIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-yellow-500" data-id="element-21122" />;
    } else {
      return <AlertTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-red-500" data-id="element-21123" />;
    }
  };
  const getSeverityClass = severity => {
    switch (severity) {
      case 'Critical':
        return 'text-red-600';
      case 'High':
        return 'text-orange-600';
      case 'Medium':
        return 'text-yellow-600';
      case 'Low':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };
  if (!selectedVendor) {
    return <div data-id="element-21124">
        <div className="mb-8 flex items-center" data-id="element-21125">
          <button onClick={() => setCurrentView('dashboard')} className="mr-4 text-gray-400 hover:text-gray-500" data-id="element-21126">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-21127" />
          </button>
          <div data-id="element-21128">
            <h3 className="text-xl font-semibold text-gray-900" data-id="element-21129">
              Compliance Issues
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-21130">
              Vendors requiring compliance review or updates
            </p>
          </div>
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-21131">
          <ul className="divide-y divide-gray-200" data-id="element-21132">
            {vendorsWithIssues.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-21133">
                No compliance issues found.
              </li> : vendorsWithIssues.map(vendor => <li key={vendor.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedVendor(vendor)} data-id="element-21134">
                  <div className="flex items-center justify-between" data-id="element-21135">
                    <div className="flex items-center" data-id="element-21136">
                      <div className="flex-shrink-0" data-id="element-21137">
                        {vendor.bankVerification && vendor.bankVerification.status === 'Failed' ? <CreditCardIcon className="h-5 w-5 text-orange-400" data-id="element-21138" /> : vendor.sanctionsCheck && vendor.sanctionsCheck.status === 'Failed' ? <ShieldIcon className="h-5 w-5 text-red-500" data-id="element-21139" /> : <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-21140" />}
                      </div>
                      <p className="ml-2 text-sm font-medium text-indigo-600 truncate" data-id="element-21141">
                        {vendor.name}
                      </p>
                    </div>
                    <div className="ml-2 flex-shrink-0" data-id="element-21142">
                      {getComplianceBadge(vendor.complianceStatus)}
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between" data-id="element-21143">
                    <div className="sm:flex" data-id="element-21144">
                      <p className="flex items-center text-sm text-gray-500" data-id="element-21145">
                        {vendor.issue}
                      </p>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-21146">
                      <p data-id="element-21147">
                        Last updated:{' '}
                        <span className="ml-1.5" data-id="element-21148">{vendor.lastUpdated}</span>
                      </p>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
      </div>;
  }
  return <div data-id="element-21149">
      <div className="mb-8 flex items-center" data-id="element-21150">
        <button onClick={() => setSelectedVendor(null)} className="mr-4 text-gray-400 hover:text-gray-500" data-id="element-21151">
          <ArrowLeftIcon className="h-5 w-5" data-id="element-21152" />
        </button>
        <div data-id="element-21153">
          <h3 className="text-xl font-semibold text-gray-900" data-id="element-21154">
            Compliance Review: {selectedVendor.name}
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-21155">
            {selectedVendor.id} • Current Status:{' '}
            {getComplianceBadge(selectedVendor.complianceStatus)}
          </p>
        </div>
      </div>
      {/* Compliance Issues */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21156">
        <div className="px-4 py-5 sm:px-6" data-id="element-21157">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21158">
            Compliance Issues
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-21159">
            Issues that need to be addressed
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-21160">
          {selectedVendor.complianceStatus === 'Compliant' && (!selectedVendor.bankVerification || selectedVendor.bankVerification.status === 'Verified') && (!selectedVendor.sanctionsCheck || selectedVendor.sanctionsCheck.status !== 'Failed') ? <div className="text-sm text-green-600 flex items-center" data-id="element-21161">
              <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-21162" />
              This vendor is currently compliant with all requirements.
            </div> : <div className="space-y-4" data-id="element-21163">
              {/* Document issues */}
              {selectedVendor.documents && selectedVendor.documents.some(doc => doc.status === 'Expired') && <div className="text-sm text-red-600 flex items-start" data-id="element-21164">
                    <AlertTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-21165" />
                    <div data-id="element-21166">
                      <p className="font-medium" data-id="element-21167">Expired Documents:</p>
                      <ul className="list-disc list-inside ml-4 mt-1" data-id="element-21168">
                        {selectedVendor.documents.filter(doc => doc.status === 'Expired').map(doc => <li key={doc.id} data-id="element-21169">
                              {doc.name} (Expired on {doc.expiryDate})
                            </li>)}
                      </ul>
                    </div>
                  </div>}
              {/* Missing documents */}
              {(!selectedVendor.documents || selectedVendor.documents.length === 0) && <div className="text-sm text-red-600 flex items-start" data-id="element-21170">
                  <AlertTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-21171" />
                  <div data-id="element-21172">
                    <p className="font-medium" data-id="element-21173">Missing Required Documents:</p>
                    <p className="mt-1" data-id="element-21174">
                      This vendor has not uploaded any required documents.
                    </p>
                  </div>
                </div>}
              {/* Bank verification issues */}
              {selectedVendor.bankVerification && selectedVendor.bankVerification.issues && selectedVendor.bankVerification.issues.length > 0 && <div className="text-sm text-orange-600 flex items-start" data-id="element-21175">
                    <CreditCardIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-21176" />
                    <div data-id="element-21177">
                      <p className="font-medium" data-id="element-21178">Bank Verification Issues:</p>
                      <ul className="list-disc list-inside ml-4 mt-1" data-id="element-21179">
                        {selectedVendor.bankVerification.issues.map((issue, index) => <li key={index} className={getSeverityClass(issue.severity)} data-id="element-21180">
                              {issue.description} ({issue.severity} severity)
                            </li>)}
                      </ul>
                      {selectedVendor.bankVerification.lastChecked && <p className="mt-1 text-xs text-gray-500" data-id="element-21181">
                          Last checked:{' '}
                          {selectedVendor.bankVerification.lastChecked}
                        </p>}
                    </div>
                  </div>}
              {/* Sanctions check issues */}
              {selectedVendor.sanctionsCheck && selectedVendor.sanctionsCheck.issues && selectedVendor.sanctionsCheck.issues.length > 0 && <div className="text-sm text-red-600 flex items-start" data-id="element-21182">
                    <ShieldIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-21183" />
                    <div data-id="element-21184">
                      <p className="font-medium" data-id="element-21185">Sanctions Check Issues:</p>
                      <ul className="list-disc list-inside ml-4 mt-1" data-id="element-21186">
                        {selectedVendor.sanctionsCheck.issues.map((issue, index) => <li key={index} className={getSeverityClass(issue.severity)} data-id="element-21187">
                              {issue.description} ({issue.severity} severity)
                            </li>)}
                      </ul>
                      {selectedVendor.sanctionsCheck.lastChecked && <p className="mt-1 text-xs text-gray-500" data-id="element-21188">
                          Last checked:{' '}
                          {selectedVendor.sanctionsCheck.lastChecked}
                        </p>}
                    </div>
                  </div>}
              {/* Pending review status */}
              {selectedVendor.complianceStatus === 'Pending Review' && <div className="text-sm text-yellow-600 flex items-start" data-id="element-21189">
                  <ClockIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-21190" />
                  <div data-id="element-21191">
                    <p className="font-medium" data-id="element-21192">Documents Pending Review:</p>
                    <p className="mt-1" data-id="element-21193">
                      Recently uploaded documents need to be reviewed.
                    </p>
                  </div>
                </div>}
            </div>}
        </div>
      </div>
      {/* Bank Account Verification */}
      {selectedVendor.bankVerification && <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21194">
          <div className="px-4 py-5 sm:px-6" data-id="element-21195">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21196">
              Bank Account Verification
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-21197">
              External verification results from Eftsure
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5" data-id="element-21198">
            <div className="flex items-center justify-between mb-4" data-id="element-21199">
              <div className="flex items-center" data-id="element-21200">
                <CreditCardIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-21201" />
                <span className="text-sm font-medium text-gray-900" data-id="element-21202">
                  Verification Status:
                </span>
              </div>
              <div data-id="element-21203">
                {getComplianceBadge(selectedVendor.bankVerification.status)}
              </div>
            </div>
            {selectedVendor.bankVerification.lastChecked && <div className="text-sm text-gray-500 mb-4" data-id="element-21204">
                Last verified: {selectedVendor.bankVerification.lastChecked}
              </div>}
            {selectedVendor.bankVerification.issues && selectedVendor.bankVerification.issues.length > 0 ? <div className="mt-4" data-id="element-21205">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-21206">
                  Verification Issues:
                </h4>
                <ul className="space-y-2" data-id="element-21207">
                  {selectedVendor.bankVerification.issues.map((issue, index) => <li key={index} className={`flex items-start ${getSeverityClass(issue.severity)}`} data-id="element-21208">
                        {getIssueIcon(issue)}
                        <div data-id="element-21209">
                          <p className="font-medium" data-id="element-21210">
                            {issue.severity} Severity:
                          </p>
                          <p data-id="element-21211">{issue.description}</p>
                        </div>
                      </li>)}
                </ul>
              </div> : selectedVendor.bankVerification.status === 'Verified' ? <div className="text-sm text-green-600 flex items-center" data-id="element-21212">
                <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-21213" />
                Bank account details have been verified successfully with
                Eftsure.
              </div> : <div className="text-sm text-gray-500" data-id="element-21214">
                No verification has been performed yet.
              </div>}
            <div className="mt-4 flex justify-end" data-id="element-21215">
              <button type="button" className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21216">
                Verify Bank Details
              </button>
            </div>
          </div>
        </div>}
      {/* Sanctions and Watchlist Check */}
      {selectedVendor.sanctionsCheck && <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21217">
          <div className="px-4 py-5 sm:px-6" data-id="element-21218">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21219">
              Sanctions & Watchlist Check
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-21220">
              Results from international sanctions and watchlist screening
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5" data-id="element-21221">
            <div className="flex items-center justify-between mb-4" data-id="element-21222">
              <div className="flex items-center" data-id="element-21223">
                <ShieldIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-21224" />
                <span className="text-sm font-medium text-gray-900" data-id="element-21225">
                  Check Status:
                </span>
              </div>
              <div data-id="element-21226">
                {getComplianceBadge(selectedVendor.sanctionsCheck.status)}
              </div>
            </div>
            {selectedVendor.sanctionsCheck.lastChecked && <div className="text-sm text-gray-500 mb-4" data-id="element-21227">
                Last checked: {selectedVendor.sanctionsCheck.lastChecked}
              </div>}
            {selectedVendor.sanctionsCheck.issues && selectedVendor.sanctionsCheck.issues.length > 0 ? <div className="mt-4" data-id="element-21228">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-21229">
                  Detected Issues:
                </h4>
                <ul className="space-y-2" data-id="element-21230">
                  {selectedVendor.sanctionsCheck.issues.map((issue, index) => <li key={index} className={`flex items-start ${getSeverityClass(issue.severity)}`} data-id="element-21231">
                      {getIssueIcon(issue)}
                      <div data-id="element-21232">
                        <p className="font-medium" data-id="element-21233">
                          {issue.severity} Severity:
                        </p>
                        <p data-id="element-21234">{issue.description}</p>
                      </div>
                    </li>)}
                </ul>
              </div> : <div className="text-sm text-green-600 flex items-center" data-id="element-21235">
                <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-21236" />
                No sanctions or watchlist matches found.
              </div>}
            <div className="mt-4 flex justify-end" data-id="element-21237">
              <button type="button" className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21238">
                Run New Check
              </button>
            </div>
          </div>
        </div>}
      {/* Documents */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21239">
        <div className="px-4 py-5 sm:px-6" data-id="element-21240">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21241">
            Documents
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-21242">
            Review vendor documentation
          </p>
        </div>
        <div className="border-t border-gray-200" data-id="element-21243">
          {selectedVendor.documents && selectedVendor.documents.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-21244">
              {selectedVendor.documents.map(doc => <li key={doc.id} className="px-4 py-4" data-id="element-21245">
                  <div className="flex items-center justify-between" data-id="element-21246">
                    <div className="flex items-center" data-id="element-21247">
                      <FileTextIcon className="h-5 w-5 text-gray-400 mr-3" data-id="element-21248" />
                      <div data-id="element-21249">
                        <h4 className="text-base font-medium text-gray-900" data-id="element-21250">
                          {doc.name}
                        </h4>
                        <div className="mt-1 flex flex-wrap items-center text-sm text-gray-500" data-id="element-21251">
                          <span className="mr-3" data-id="element-21252">Type: {doc.type}</span>
                          <span className="mr-3" data-id="element-21253">
                            Uploaded: {doc.uploadDate}
                          </span>
                          {doc.expiryDate && <span className="mr-3" data-id="element-21254">
                              <CalendarIcon className="inline-block mr-1 h-4 w-4 text-gray-400" data-id="element-21255" />
                              Expires: {doc.expiryDate}
                            </span>}
                        </div>
                      </div>
                    </div>
                    <div data-id="element-21256">{getDocumentStatusBadge(doc.status)}</div>
                  </div>
                </li>)}
            </ul> : <div className="px-4 py-5 text-center text-sm text-gray-500" data-id="element-21257">
              No documents uploaded yet.
            </div>}
        </div>
      </div>
      {/* Upload New Documents */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21258">
        <div className="px-4 py-5 sm:px-6" data-id="element-21259">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21260">
            Upload New Documents
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-21261">
            Add updated compliance documents
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-21262">
          <FileUpload onFileUpload={handleFileUpload} data-id="element-21263" />
          {files.length > 0 && <div className="mt-4" data-id="element-21264">
              <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-21265">
                New Files to Upload
              </h4>
              <ul className="border rounded-md divide-y divide-gray-200" data-id="element-21266">
                {files.map((file, index) => <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" data-id="element-21267">
                    <div className="flex items-center" data-id="element-21268">
                      <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-21269" />
                      <span className="ml-2 flex-1 w-0 truncate" data-id="element-21270">
                        {file.name}
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-21271">
                      <span className="text-xs text-gray-500 mr-4" data-id="element-21272">
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                      <button type="button" onClick={() => removeFile(index)} className="text-red-600 hover:text-red-900" data-id="element-21273">
                        <XIcon className="h-4 w-4" data-id="element-21274" />
                      </button>
                    </div>
                  </li>)}
              </ul>
            </div>}
        </div>
      </div>
      {/* Send Message */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21275">
        <div className="px-4 py-5 sm:px-6" data-id="element-21276">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21277">
            Send Message to Vendor
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-21278">
            Request updates or provide information
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-21279">
          <textarea rows={4} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Enter message to vendor..." value={message} onChange={e => setMessage(e.target.value)} data-id="element-21280" />
          <div className="mt-3 flex justify-end" data-id="element-21281">
            <button type="button" onClick={handleSendReminder} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" disabled={!message} data-id="element-21282">
              <SendIcon className="h-4 w-4 mr-1" data-id="element-21283" />
              Send Message
            </button>
          </div>
        </div>
      </div>
      {/* Update Compliance Status */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21284">
        <div className="px-4 py-5 sm:px-6" data-id="element-21285">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21286">
            Update Compliance Status
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-21287">
            Change the vendor's compliance status
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-21288">
          <div className="flex flex-wrap gap-3" data-id="element-21289">
            <button type="button" onClick={() => handleUpdateStatus('Compliant')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-21290">
              <CheckIcon className="h-4 w-4 mr-1" data-id="element-21291" />
              Mark as Compliant
            </button>
            <button type="button" onClick={() => handleUpdateStatus('Pending Review')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500" data-id="element-21292">
              <ClockIcon className="h-4 w-4 mr-1" data-id="element-21293" />
              Mark as Pending Review
            </button>
            <button type="button" onClick={() => handleUpdateStatus('Non-Compliant')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-21294">
              <XIcon className="h-4 w-4 mr-1" data-id="element-21295" />
              Mark as Non-Compliant
            </button>
          </div>
        </div>
      </div>
    </div>;
}